import React from 'react'
import styled from 'styled-components'
import { asUnified } from '@devnw/theme'

/**
 * Shared tenant-resolution gate used by every layout that must hold its
 * children behind `useActiveTenant().tenant` resolving to a non-empty value
 * (currently AdminLayout and BareLayout — see each for why the gate exists:
 * mounting a page with an empty tenantId fires a request that the server or
 * router mishandles).
 *
 * Originally implemented twice (once per layout) as an unbounded spinner
 * that never terminated when resolution failed — see 291c525f, which fixed
 * this for AdminLayout only. Extracted here so BOTH layouts share ONE
 * implementation and can't drift out of sync again.
 */

const CenteredLoader = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 40vh;
`

const TenantErrorMessage = styled.div`
  max-width: 480px;
  text-align: center;
  color: ${({ theme }) => asUnified(theme).text?.primary ?? '#0f172a'};
  font-size: ${({ theme }) => asUnified(theme).font?.sizes?.sm ?? '0.875rem'};
`

/**
 * Zero-dependency spinner. @devnw/primitives' LogoLoader would be the
 * natural fit here, but its `Loading` subpath barrel also re-exports
 * `Spinner`, which imports `WaSpinner` from `@devnw/webawesome` — that
 * package ships a pre-built `vendor/dist/react/index.js` as raw untranspiled
 * ESM with no `.ts`/`.tsx` extension, which falls outside this app's jest
 * `transform` map (only `.tsx?` is configured) even though
 * `transformIgnorePatterns` allows `@devnw/*` through the ignore list —
 * `SyntaxError: Cannot use import statement outside a module` the moment any
 * test imports that subpath. A toolchain gap, not fixable within the six
 * migrated files, so a local spinner is used instead (see
 * src/components/PamStateComponents.tsx for the identical pattern).
 */
const SpinnerRing = styled.div<{ $size: number }>`
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 50%;
  border: 3px solid
    ${({ theme }) => `${asUnified(theme).colors?.primary ?? '#1a56db'}33`};
  border-top-color: ${({ theme }) => asUnified(theme).colors?.primary ?? '#1a56db'};
  animation: pam-spin 0.9s linear infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  @keyframes pam-spin {
    to {
      transform: rotate(360deg);
    }
  }
`

/**
 * Renders the tenant-not-yet-resolved state: a bounded spinner while a
 * resolution attempt (claims fallback or refreshTenants()) is genuinely in
 * flight, or — once that resolution is exhausted — an accessible, terminal
 * `role="alert"` message. Spinning forever regardless of outcome was the
 * bug; extracted to a component (rather than a nested ternary) to keep the
 * two states easy to tell apart at a glance.
 */
export const TenantGate = ({ tenantError }: { tenantError: string | null }) => {
  if (tenantError !== null) {
    return (
      <CenteredLoader>
        <TenantErrorMessage role="alert">
          {tenantError} No tenant is available for your account.
        </TenantErrorMessage>
      </CenteredLoader>
    )
  }
  return (
    <CenteredLoader>
      <SpinnerRing role="status" aria-live="polite" aria-label="Loading tenant" $size={40} />
    </CenteredLoader>
  )
}
